import { api } from '../api/client';

import { API_COLLECTIONS_URL, API_ITEMS_URL } from '../utils';

/**
 * ITEM TAGS — turning a card's items INTO tags (docs/plans/tags.md §3.2,
 * §3.8, ruled d29–d39 and B19; keepr-api docs/SCHEMA.md § Item tags; build
 * step T4d). Not the pre-T1 module of this name (retired with the legacy
 * tags): nothing here reads or writes the tags ON an item — those are
 * `tagIds` on the ordinary item writes (services/items.js) and the private
 * store (services/tagApplications.js).
 *
 * - A card's setting per collection — `GET` / `POST
 *   /api/collections/{id}/cards/{cardId}/item-tags` — Off · Chosen items ·
 *   Every item, and Apply where referenced. The GET is a meta-read; its
 *   `canManage` says whether the caller may change it (manage, and the
 *   collection not archived). The POST's rate class is `itemTagMode`: 20
 *   writes an hour per person — dry runs are not counted (keepr-api T6b),
 *   though every call meets a door of 120 an hour.
 * - Apply where referenced ON ITS OWN — the same POST with
 *   `{ applyWhereReferenced, dryRun? }` and no `mode` (keepr-api T6b):
 *   nothing is re-counted and no backfill restarts. Six switches of it an
 *   hour per card (429 `rate_limited`, `limit: 6`).
 * - One item's tag — `PUT /api/items/{id}/own-tag` (Use as a tag) and `POST
 *   /api/items/{id}/own-tag/disable` (Stop using as a tag: the off dialog's
 *   choices, with a dry run for its totals and the name a keep publishes).
 *   Manager of the item's collection (ruled d35b).
 *
 * Refusals keep `error.response.data` (`code`, `count`, …) for
 * utils/itemTagSettings.js itemTagRefusal to word.
 */

const cardItemTagsUrl = (collectionId, cardId) => `${API_COLLECTIONS_URL}/${collectionId}/cards/${cardId}/item-tags`;
const ownTagUrl = itemId => `${API_ITEMS_URL}/${itemId}/own-tag`;

/**
 * Where a card stands in a collection: `{ mode, applyWhereReferenced,
 * fromCardId, ownMode, count, canManage, pass?, refsPass? }` — the effective
 * mode (the nearest card up its chain with an entry decides), the card it
 * comes from, this card's own, how many of its item tags YOU can read,
 * whether you may turn this collection's item tags on and off (`canManage`:
 * manage, and the collection not archived), and — for a manager only — the
 * latest pass and the latest where-referenced pass (`pass` / `refsPass`
 * present, possibly null; `refsPass.waiting` is `item_tags_backfill` or
 * `collection_archived` while it cannot run yet).
 */
export async function getCardItemTags(collectionId, cardId) {
	const { data } = await api.get(cardItemTagsUrl(collectionId, cardId));
	return data || {};
}

/**
 * Sets a card's mode here. `body`: `{ mode, applyWhereReferenced?, confirm?,
 * dryRun?, automatic?, hand?, confirmNames?, cleanup? }`.
 *
 * - `every` + `dryRun` → `{ dryRun, mode, count, warnAt, limit, warn, refused }`
 *   (409 `too_many_items` over 10,000 even on a dry run); over 1,000 the real
 *   call needs `confirm: true` (409 `confirm_required`).
 * - `off` + `dryRun` → `{ dryRun, mode, totals: { tags, items, hand, automatic,
 *   deletedItemTags } }`; with a kept choice also `names: [{ itemId, tagId,
 *   name }]` and `confirmNames`, which the real call must echo (409
 *   `confirm_names_required`, owner call B19).
 * → `{ mode, applyWhereReferenced, count?, totals?, pass, refsPass?, cleanup }`.
 */
export async function setCardItemTags(collectionId, cardId, body = {}) {
	const { data } = await api.post(cardItemTagsUrl(collectionId, cardId), body);
	return data || {};
}

/**
 * Apply where referenced, ON ITS OWN (keepr-api T6b): the POST with
 * `{ applyWhereReferenced }` and NO `mode` — nothing else about the card
 * changes, nothing is re-counted, no backfill restarts. The card's own entry
 * must be Chosen items or Every item (409 `item_tags_off`: an inherited
 * entry is switched on the card it comes from); any other key is 400
 * `invalid_mode`; the seventh switch in an hour for the card is 429
 * `rate_limited` (`retryAfter`, `limit: 6`).
 *
 * - `dryRun: true` → `{ dryRun, mode, applyWhereReferenced, change, automatic }`
 *   — `automatic`, the items carrying one of the card's tags automatically:
 *   what turning it off takes the tag off.
 * - Otherwise → `{ mode, applyWhereReferenced, refsPass }`: on starts the
 *   where-referenced pass, off its pull (hand applications stay).
 */
export async function setCardWhereReferenced(collectionId, cardId, applyWhereReferenced, { dryRun = false } = {}) {
	const body = { applyWhereReferenced: applyWhereReferenced === true };
	if (dryRun) body.dryRun = true;
	const { data } = await api.post(cardItemTagsUrl(collectionId, cardId), body);
	return data || {};
}

/**
 * The impact report for every item tag of a card here (its Off dialog, when
 * both kinds of application are removed): `{ references, truncated, tags }`.
 * Manager only.
 */
export async function getCardItemTagReferences(collectionId, cardId) {
	const { data } = await api.get(`${cardItemTagsUrl(collectionId, cardId)}/references`);
	return {
		references: Array.isArray(data?.references) ? data.references : [],
		truncated: Boolean(data?.truncated),
		tags: Number(data?.tags) || 0,
	};
}

/**
 * Use as a tag: turns the item's tag on (its card must be Chosen items or
 * Every item here). Idempotent. → the item tag (`{ _id, kind: 'item',
 * item_id, card_id, parentId, position, status, title }`).
 */
export async function turnOnItemTag(itemId, { parentId, position } = {}) {
	const body = {};
	if (parentId !== undefined) body.parentId = parentId;
	if (position !== undefined) body.position = position;
	const { data } = await api.put(ownTagUrl(itemId), body);
	return data?.payload || null;
}

/**
 * Stop using as a tag — the off dialog (ruled d30): `{ automatic, hand }`
 * are 'remove' | 'keep'. Keeping either CONVERTS the tag into a collection
 * tag (the id kept), named `name` and wearing `icon` / `color` when given
 * (else the item's title and the card's icon and color — d36); removing both
 * DELETES it, and `cleanup` carries the impact report's "Remove for me"
 * choices (`[{ holder, id }]`). → `{ converted: Tag | null, deleted,
 * applications, privateUsesRemoved, cleanup }`.
 *
 * `dryRun: true` writes nothing (the choices may be left out) → `{ dryRun,
 * totals: { tags: 1, items, hand, automatic, deletedItemTags: 0 } }`, and
 * with a kept choice also `names: [{ itemId, tagId, name }]` and
 * `confirmNames`. A real call that keeps must echo that digest as
 * `confirmNames`, else 409 `confirm_names_required` (`count: 1`; owner call
 * B19, as a card-wide keep). A removal needs none.
 */
export async function turnOffItemTag(itemId, { automatic, hand, name, icon, color, cleanup, dryRun, confirmNames } = {}) {
	const body = {};
	if (dryRun) body.dryRun = true;
	if (automatic !== undefined) body.automatic = automatic;
	if (hand !== undefined) body.hand = hand;
	if (name !== undefined) body.name = name;
	if (icon !== undefined) body.icon = icon;
	if (color !== undefined) body.color = color;
	if (Array.isArray(cleanup) && cleanup.length) body.cleanup = cleanup;
	if (confirmNames) body.confirmNames = confirmNames;
	const { data } = await api.post(`${ownTagUrl(itemId)}/disable`, body);
	return data || {};
}
